import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useUpdateInvestorProfileMutation } from 'api/main';
import { userSelectors } from 'reducers';
import { jsonParseSafe } from 'utils/helpers';

import EditableEmailAddress from '../communication/EditableEmailAddress';
import CommunicationEmailAdd from 'containers/communication/CommunicationEmailAdd';
import { ExpandableList } from 'components';
import { Box } from '@mui/material';

const MAX_ADDRESSES = 20;

const InvestorEmailList = ({ emailsKey, title }) => {
    const mounted = useRef(false);

    const internalUser = useSelector(userSelectors.getInternalUser);

    const addresses = useMemo(
        () => jsonParseSafe(internalUser.investorProfile[emailsKey], []),
        [internalUser, emailsKey]
    );

    const [updateInvestorProfile] = useUpdateInvestorProfileMutation({
        fixedCacheKey: 'investor-emails',
    });

    const handleDeleteAddress = useCallback(
        index => () => {
            const newAddressList = addresses.filter((_, i) => i !== index);
            updateInvestorProfile({
                id: internalUser.investorProfile.id,
                emails: newAddressList,
                emailsKey,
            });
        },
        [updateInvestorProfile, addresses, internalUser, emailsKey]
    );

    const handleUpdateAddress = useCallback(
        index => newValue => {
            const newAddressList = [...addresses];
            newAddressList[index] = newValue;
            updateInvestorProfile({
                id: internalUser.investorProfile.id,
                emails: newAddressList,
                emailsKey,
            });
        },
        [updateInvestorProfile, addresses, internalUser, emailsKey]
    );

    const handleAddAddress = useCallback(
        newAddress => {
            updateInvestorProfile({
                id: internalUser.investorProfile.id,
                emails: [...addresses, newAddress],
                emailsKey,
            });
        },
        [updateInvestorProfile, addresses, internalUser, emailsKey]
    );

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return (
        <ExpandableList title={title}>
            <Box width="360px" marginX="auto">
                {addresses.length < MAX_ADDRESSES && (
                    <CommunicationEmailAdd onAdd={handleAddAddress} addresses={addresses} />
                )}
                {addresses.map((address, i) => (
                    <EditableEmailAddress
                        key={address}
                        defaultValue={address}
                        onUpdate={handleUpdateAddress(i)}
                        onDelete={handleDeleteAddress(i)}
                    />
                ))}
            </Box>
        </ExpandableList>
    );
};

export default InvestorEmailList;
